import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import type { KommunStatus } from './status';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);
export type { KommunStatus } from './status';

export interface NoticeEvent {
  id: string;
  notis_typ: "introduktion" | "uppdatering" | "upphavning";
  restriktion_typ: string | null;
  datum_fran: string | null;
  datum_till: string | null;
  datum_publicerad: string | null;
  omraden_text: string | null;
  skapad_vid: string;
  url: string;
  kommuner: { kommunkod: string; namn: string }[];
}

export async function getAllKommunStatus(): Promise<KommunStatus[]> {
  const { data, error } = await supabase
    .from('kommun_status')
    .select('*')
    .order('kommun_namn');

  if (error) throw new Error(error.message);
  return data as KommunStatus[];
}

export interface Kommun {
  kommunkod: string;
  namn: string;
  lan: string | null;
}

export async function getAllKommuner(): Promise<Kommun[]> {
  const { data, error } = await supabase
    .from('kommuner')
    .select('kommunkod, namn, lan')
    .order('namn');

  if (error) throw new Error(error.message);
  return data as Kommun[];
}

export interface KommunStatusAgg {
  kommunkod: string;
  kommun_namn: string;
  lan: string | null;
  va_namn: string | null;
  typ: "forbud" | "rekommendation" | null;
  senast_scrapad: string | null;
  har_kommande: boolean | null;
}

export async function getAllKommunStatusAgg(): Promise<KommunStatusAgg[]> {
  const { data, error } = await supabase
    .from('kommun_status_agg')
    .select('*')
    .order('kommun_namn');

  if (error) throw new Error(error.message);
  return data as KommunStatusAgg[];
}

export async function getNotices(): Promise<NoticeEvent[]> {
  const { data, error } = await supabase
    .from('notiser_with_kommuner')
    .select('*')
    .order('datum_publicerad', { ascending: false, nullsFirst: false })
    .limit(50);

  if (error) throw new Error(error.message);

  // Defensive client-side sort in case the view order is ever changed
  const sorted = (data as NoticeEvent[]).sort((a, b) => {
    if (!a.datum_publicerad && !b.datum_publicerad) return 0;
    if (!a.datum_publicerad) return 1;
    if (!b.datum_publicerad) return -1;
    return b.datum_publicerad.localeCompare(a.datum_publicerad);
  });

  return sorted;
}

export async function getKommunStatusByKod(kommunkod: string): Promise<KommunStatus[]> {
  const { data, error } = await supabase
    .from('kommun_status')
    .select('*')
    .eq('kommunkod', kommunkod)
    .order('kommun_namn');

  if (error) throw new Error(error.message);
  return data as KommunStatus[];
}

export async function getNoticesForKommun(kommunkod: string): Promise<NoticeEvent[]> {
  const { data, error } = await supabase
    .from('notiser_with_kommuner')
    .select('*')
    .order('datum_publicerad', { ascending: false, nullsFirst: false });

  if (error) throw new Error(error.message);

  const all = (data as NoticeEvent[]) ?? [];
  return all.filter((n) => n.kommuner.some((k) => k.kommunkod === kommunkod));
}

export async function getAllNoticesForKommuner(): Promise<NoticeEvent[]> {
  const { data, error } = await supabase
    .from('notiser_with_kommuner')
    .select('*')
    .order('datum_publicerad', { ascending: false, nullsFirst: false });

  if (error) throw new Error(error.message);
  return (data as NoticeEvent[]) ?? [];
}

export async function resolveLocation(lat: number, lng: number): Promise<{
  kommun_namn: string | null;
  kommunkod: string | null;
  status: KommunStatus[];
}> {
  // Use OpenStreetMap Nominatim for reverse geocoding
  const url = new URL('https://nominatim.openstreetmap.org/reverse');
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('lat', String(lat));
  url.searchParams.set('lon', String(lng));
  url.searchParams.set('zoom', '10');
  url.searchParams.set('accept-language', 'sv');

  const res = await fetch(url.toString(), {
    headers: {
      'User-Agent': 'Bevattningskartan/1.0 (https://bevattningskartan.se)',
    },
  });
  
  if (!res.ok) {
    throw new Error('Kunde inte slå upp platsen just nu');
  }
  
  const json = await res.json() as {
    address?: {
      municipality?: string;
      county?: string;
      city?: string;
      town?: string;
      village?: string;
    };
    display_name?: string;
  };

  const addr = json.address ?? {};
  const candidates = [
    addr.municipality,
    addr.city,
    addr.town,
    addr.village,
    addr.county,
  ].filter(Boolean) as string[];

  // Normalize: remove "kommun" / "Municipality" / län suffixes
  const norm = (s: string) =>
    s
      .replace(/\s*kommun$/i, '')
      .replace(/\s*municipality$/i, '')
      .replace(/s\s+län$/i, '')
      .trim()
      .toLowerCase();

  const { data: rows, error } = await supabase
    .from('kommun_status')
    .select('*');
  
  if (error) throw new Error(error.message);

  let matchedKommunkod: string | null = null;
  let matchedName: string | null = null;
  
  for (const cand of candidates) {
    const c = norm(cand);
    const found = (rows ?? []).find(
      (r) => norm((r as KommunStatus).kommun_namn) === c
    );
    if (found) {
      matchedKommunkod = (found as KommunStatus).kommunkod;
      matchedName = cand;
      break;
    }
  }

  const matchedRows = matchedKommunkod
    ? (rows ?? [])
        .filter((r) => (r as KommunStatus).kommunkod === matchedKommunkod)
        .map((r) => r as KommunStatus)
    : [];

  return {
    kommun_namn: matchedName,
    kommunkod: matchedKommunkod,
    status: matchedRows,
  };
}
